// Google Sheets analytics via Apps Script webhook.
// Set REACT_APP_SHEETS_WEBHOOK_URL in .env to your deployed Apps Script /exec URL.
// All calls are fire-and-forget — failures are silently swallowed.

const WEBHOOK = process.env.REACT_APP_SHEETS_WEBHOOK_URL;

// Persistent cross-session visitor ID (survives browser restart; per-device/browser)
function getVisitorId() {
  try {
    let id = localStorage.getItem('ftx_vid');
    if (!id) {
      id = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('ftx_vid', id);
    }
    return id;
  } catch { return 'v_unknown'; }
}

// Session-scoped ID (resets on tab close)
function getSessionId() {
  try {
    let id = sessionStorage.getItem('ftx_sid');
    if (!id) {
      id = 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('ftx_sid', id);
    }
    return id;
  } catch { return 's_unknown'; }
}

function getUtm() {
  const p = new URLSearchParams(window.location.search);
  const parts = ['source', 'medium', 'campaign', 'term', 'content']
    .map(k => p.get('utm_' + k) || '')
    .filter(Boolean);
  return parts.join(' | ') || '';
}

function base() {
  return {
    timestamp:  new Date().toISOString(),
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    referrer:   document.referrer || '',
    utm:        getUtm(),
    timezone:   Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    language:   navigator.language || '',
    screen:     `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
    platform:   navigator.platform || '',
    touch:      navigator.maxTouchPoints > 0 ? '1' : '0',
  };
}

async function send(sheet, payload) {
  if (!WEBHOOK) return;
  try {
    // text/plain avoids CORS preflight — Apps Script still receives the JSON body
    await fetch(WEBHOOK, {
      method:  'POST',
      mode:    'no-cors',
      headers: { 'Content-Type': 'text/plain' },
      body:    JSON.stringify({ sheet, ...payload }),
    });
  } catch {
    // non-blocking — analytics must never break the UI
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Call after a successful account creation (OTP verified). */
export function logSignup({ email, name, source = '' }) {
  send('signups', {
    ...base(),
    email,
    name,
    source: source || new URLSearchParams(window.location.search).get('from') || '',
    page:   window.location.pathname,
  });
  pushGTM('signup_completed', { source });
}

/**
 * Call immediately when signup form is submitted (before OTP).
 * Captures email early — useful when OTP emails don't arrive.
 */
export function logSignupAttempt({ email, name }) {
  send('signups', {
    ...base(),
    email,
    name,
    source: new URLSearchParams(window.location.search).get('from') || '',
    page:   window.location.pathname,
    status: 'otp_pending',
  });
}

/** Call on every route change. */
export function logPageLoad({ path, title = '', userEmail = '' }) {
  send('pageloads', {
    ...base(),
    path,
    title:      title || document.title,
    user_email: userEmail,
  });
}

/** Call when a contact form is submitted. Also triggers email notification via Apps Script. */
export function logContact({ name, company, phone, email, topic, message }) {
  send('contacts', {
    ...base(),
    name, company, phone, email, topic, message,
  });
}

/** Send a named event directly to GA4 via gtag (loaded by GTM). */
function pushGTM(eventName, params = {}) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...params });
    }
  } catch { }
}

/** Generic event — button clicks, feature usage, etc. */
export function logEvent({ name, type = 'click', page = '', userEmail = '', meta = {} }) {
  send('events', {
    ...base(),
    event_name: name,
    event_type: type,
    page:       page || window.location.pathname,
    user_email: userEmail,
    meta:       JSON.stringify(meta),
  });
  pushGTM(name, { event_type: type, ...meta });
}
